/**
 * Página CrmDealDetails - Detalhes completos do Deal
 * Layout em 2 colunas com informações e timeline
 * Versão: 1.0.0
 */
import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { 
  ArrowLeft, 
  Pencil, 
  Trophy, 
  XCircle, 
  RotateCcw,
  Building2,
  User,
  Calendar,
  Target,
  Trash2
} from 'lucide-react';
import { useCrmDeal, useCrmDeals } from '@/hooks/useCrmDeals';
import { DealFormDialog } from '@/components/crm/deals/DealFormDialog';
import { DealStatusBadge } from '@/components/crm/deals/DealStatusBadge';
import { DealPriorityBadge } from '@/components/crm/deals/DealPriorityBadge';
import { DealActivityTimeline } from '@/components/crm/deals/DealActivityTimeline';
import { DealActivityForm } from '@/components/crm/deals/DealActivityForm';
import { DealItemsList } from '@/components/crm/deals/DealItemsList';
import { PhaseSelector } from '@/components/crm/PhaseSelector';
import { PriceDisplay } from '@/components/crm/PriceDisplay';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

/**
 * Página de detalhes do Deal
 */
export default function CrmDealDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [formOpen, setFormOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const { data: deal, isLoading, error } = useCrmDeal(id || null);
  const { 
    moveDealToPhase, 
    markAsWon,
    markAsLost, 
    reopenDeal,
    deleteDeal 
  } = useCrmDeals(undefined, { enabled: false }); // Apenas mutations — evita SELECT * desnecessário

  // Muda fase do deal
  const handlePhaseChange = (newPhaseId: string) => {
    if (!id || !deal || newPhaseId === deal.phase_id) return;
    moveDealToPhase.mutate({ dealId: id, newPhaseId });
  };

  // Deleta deal
  const handleDelete = async () => {
    if (!id) return;
    await deleteDeal.mutateAsync(id);
    navigate('/tenant/crm/deals');
  };

  // Gera iniciais
  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  };

  if (isLoading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Skeleton className="h-48" />
            <Skeleton className="h-64" />
          </div>
          <div className="space-y-6">
            <Skeleton className="h-48" />
            <Skeleton className="h-96" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !deal) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <p className="text-muted-foreground">Deal não encontrado.</p>
        <Button variant="outline" onClick={() => navigate('/tenant/crm/deals')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 border-b">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold">{deal.title}</h1>
              <DealStatusBadge status={deal.status} />
            </div>
            {(deal.company_name || deal.contact?.name) && (
              <p className="text-sm text-muted-foreground mt-1">
                {deal.company_name || deal.contact?.name}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setFormOpen(true)}>
            <Pencil className="h-4 w-4 mr-2" />
            Editar
          </Button>
          {deal.status === 'open' ? (
            <>
              <Button 
                variant="outline" 
                className="text-green-600 border-green-600 hover:bg-green-50"
                onClick={() => markAsWon.mutate(deal.id)}
              >
                <Trophy className="h-4 w-4 mr-2" />
                Ganho
              </Button>
              <Button 
                variant="outline"
                className="text-red-600 border-red-600 hover:bg-red-50"
                onClick={() => markAsLost.mutate({ dealId: deal.id })}
              >
                <XCircle className="h-4 w-4 mr-2" />
                Perdido
              </Button>
            </>
          ) : (
            <Button variant="outline" onClick={() => reopenDeal.mutate(deal.id)}>
              <RotateCcw className="h-4 w-4 mr-2" />
              Reabrir
            </Button>
          )}
          <Button 
            variant="ghost" 
            size="icon"
            className="text-destructive"
            onClick={() => setDeleteOpen(true)}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Conteúdo */}
      <div className="flex-1 overflow-auto p-4">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Coluna Principal */}
          <div className="lg:col-span-2 space-y-6">
            {/* Informações do Deal */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Informações</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Valor</p>
                  <PriceDisplay amount={deal.value} currency={deal.currency} size="lg" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Probabilidade</p>
                  <p className="font-medium">{deal.probability ?? 0}%</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Prioridade</p>
                  <DealPriorityBadge priority={deal.priority} />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Pipeline</p>
                  <p className="font-medium">{deal.pipeline?.name}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-sm text-muted-foreground mb-1">Fase Atual</p>
                  <PhaseSelector
                    pipelineId={deal.pipeline_id}
                    value={deal.phase_id}
                    onSelect={handlePhaseChange}
                    disabled={deal.status !== 'open'}
                  />
                </div>

                <Separator className="col-span-2" />

                {deal.contact && (
                  <div className="col-span-2">
                    <p className="text-sm text-muted-foreground mb-2">Contato</p>
                    <div className="flex items-center gap-3">
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={deal.contact.avatar_url || undefined} />
                        <AvatarFallback>
                          {deal.contact.name ? getInitials(deal.contact.name) : '?'}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium">{deal.contact.name || 'Sem nome'}</p>
                        <p className="text-sm text-muted-foreground">
                          {[deal.contact.phone, deal.contact.email].filter(Boolean).join(' • ')}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {deal.assigned_user && (
                  <div className="col-span-2">
                    <p className="text-sm text-muted-foreground mb-2">Responsável</p>
                    <div className="flex items-center gap-3">
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={deal.assigned_user.avatar_url || undefined} />
                        <AvatarFallback>
                          {getInitials(deal.assigned_user.full_name)}
                        </AvatarFallback>
                      </Avatar>
                      <p className="font-medium">{deal.assigned_user.full_name}</p>
                    </div>
                  </div>
                )}

                <div>
                  <p className="text-sm text-muted-foreground mb-1">Data Prevista</p>
                  <p className="font-medium">
                    {deal.expected_close_date 
                      ? format(new Date(deal.expected_close_date), 'dd/MM/yyyy', { locale: ptBR })
                      : '-'}
                  </p>
                </div>

                <div>
                  <p className="text-sm text-muted-foreground mb-1">Origem</p>
                  <p className="font-medium">{deal.source || '-'}</p>
                </div>

                {deal.description && (
                  <div className="col-span-2">
                    <p className="text-sm text-muted-foreground mb-1">Descrição</p>
                    <p className="text-sm">{deal.description}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Produtos/Serviços */}
            <DealItemsList 
              dealId={deal.id} 
              currency={deal.currency}
              readOnly={deal.status !== 'open'}
            />
          </div>

          {/* Coluna Lateral - Atividades */}
          <div className="space-y-6">
            {/* Formulário de nova atividade */}
            {deal.status === 'open' && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Nova Atividade</CardTitle>
                </CardHeader>
                <CardContent>
                  <DealActivityForm dealId={deal.id} compact />
                </CardContent>
              </Card>
            )}

            {/* Timeline de atividades */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Histórico</CardTitle>
              </CardHeader>
              <CardContent>
                <DealActivityTimeline dealId={deal.id} />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Dialog de edição */}
      <DealFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        deal={deal}
      />

      {/* Dialog de confirmação de exclusão */}
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir Negócio</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir "{deal.title}"? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
